import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { App, type MenuProps } from 'antd'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'

export type ExpenseBusinessActionRow = {
  id: number
  isEnabled: boolean
}

type ExpenseBusinessListActionsOptions = {
  currentPage: number
  rows: ExpenseBusinessActionRow[]
  invalidate: () => Promise<unknown>
  goToPreviousPage: () => Promise<unknown>
}

export function useExpenseBusinessListActions({
  currentPage,
  goToPreviousPage,
  invalidate,
  rows
}: ExpenseBusinessListActionsOptions) {
  const trpc = useTRPC()
  const { message, modal } = App.useApp()
  const [selectedExpenseBusinessIds, setSelectedExpenseBusinessIds] = useState<number[]>([])
  const setEnabled = useMutation(trpc.expenseBusiness.setEnabled.mutationOptions())
  const batchSetEnabled = useMutation(trpc.expenseBusiness.batchSetEnabled.mutationOptions())
  const remove = useMutation(trpc.expenseBusiness.remove.mutationOptions())
  const batchRemove = useMutation(trpc.expenseBusiness.batchRemove.mutationOptions())

  const clearSelection = () => setSelectedExpenseBusinessIds([])

  const changeStatus = async (expenseBusiness: ExpenseBusinessActionRow) => {
    try {
      await setEnabled.mutateAsync({ id: expenseBusiness.id, isEnabled: !expenseBusiness.isEnabled })
      void message.success(expenseBusiness.isEnabled ? '支出业务已停用' : '支出业务已启用')
      await invalidate()
    } catch (error) {
      void message.error(getErrorMessage(error, '支出业务状态更新失败'))
    }
  }

  const changeSelectedStatus = async (isEnabled: boolean) => {
    try {
      const result = await batchSetEnabled.mutateAsync({ ids: selectedExpenseBusinessIds, isEnabled })
      clearSelection()
      void message.success(`已${isEnabled ? '启用' : '停用'} ${result.count} 个支出业务`)
      await invalidate()
    } catch (error) {
      void message.error(getErrorMessage(error, '批量更新支出业务状态失败'))
    }
  }

  const removeExpenseBusiness = async (expenseBusiness: ExpenseBusinessActionRow) => {
    try {
      await remove.mutateAsync({ id: expenseBusiness.id })
      void message.success('支出业务已删除')
      const shouldGoBack = rows.length === 1 && currentPage > 1
      await invalidate()
      if (shouldGoBack) {
        await goToPreviousPage()
      }
    } catch (error) {
      void message.error(getErrorMessage(error, '支出业务删除失败'))
    }
  }

  const confirmRemoveExpenseBusiness = (expenseBusiness: ExpenseBusinessActionRow) => {
    modal.confirm({
      content: '删除后将从支出业务列表和可选支出业务中移除，历史账单数据仍会保留。',
      okButtonProps: { danger: true },
      onOk: () => removeExpenseBusiness(expenseBusiness),
      title: '确认删除该支出业务？'
    })
  }

  const confirmChangeStatus = (expenseBusiness: ExpenseBusinessActionRow) => {
    modal.confirm({
      content: expenseBusiness.isEnabled ? '停用后将不能用于新的支出账单，历史数据仍会保留。' : undefined,
      okButtonProps: expenseBusiness.isEnabled ? { danger: true } : undefined,
      onOk: () => changeStatus(expenseBusiness),
      title: `确认${expenseBusiness.isEnabled ? '停用' : '启用'}支出业务？`
    })
  }

  const removeSelectedExpenseBusinesses = async () => {
    try {
      const result = await batchRemove.mutateAsync({ ids: selectedExpenseBusinessIds })
      const allVisibleRowsSelected = selectedExpenseBusinessIds.length === rows.length && rows.length > 0
      clearSelection()
      void message.success(`已删除 ${result.count} 个支出业务`)
      await invalidate()
      if (allVisibleRowsSelected && currentPage > 1) {
        await goToPreviousPage()
      }
    } catch (error) {
      void message.error(getErrorMessage(error, '批量删除支出业务失败'))
    }
  }

  const confirmChangeSelectedStatus = (isEnabled: boolean) => {
    if (isEnabled) {
      void changeSelectedStatus(true)
      return
    }
    modal.confirm({
      content: '停用后，选中的支出业务不能继续用于新的支出账单，历史数据仍会保留。',
      okButtonProps: { danger: true },
      okText: '确认停用',
      onOk: () => changeSelectedStatus(false),
      title: `停用已选 ${selectedExpenseBusinessIds.length} 个支出业务？`
    })
  }

  const confirmRemoveSelectedExpenseBusinesses = () => {
    modal.confirm({
      content: '删除后将从支出业务列表和可选支出业务中移除，历史账单数据仍会保留。',
      okButtonProps: { danger: true },
      okText: '确认删除',
      onOk: removeSelectedExpenseBusinesses,
      title: `删除已选 ${selectedExpenseBusinessIds.length} 个支出业务？`
    })
  }

  const batchMenu: MenuProps = {
    items: [
      { key: 'enable', label: '批量启用' },
      { danger: true, key: 'disable', label: '批量停用' },
      { type: 'divider' as const },
      { danger: true, key: 'delete', label: '批量删除' }
    ],
    onClick: ({ key }) => {
      if (key === 'delete') {
        confirmRemoveSelectedExpenseBusinesses()
        return
      }
      confirmChangeSelectedStatus(key === 'enable')
    }
  }

  return {
    batchMenu,
    clearSelection,
    confirmChangeStatus,
    confirmRemoveExpenseBusiness,
    selectedExpenseBusinessIds,
    setSelectedExpenseBusinessIds
  }
}
